<template>
  <div>
    <div v-show="chatWindowMaximized" v-if="showChatWindow">
      <ChatWindow
        :dealer-id="conversation.dealerId"
        :dealer-name="conversation.dealerName"
        :ad-id="conversation.adId"
        :ad-url="conversation.adUrl"
        :pop-up-z-index="conversation.popUpZIndex"
        :page-url-override="conversation.pageUrlOverride"
        :chat-app-url="chatAppUrl"
        :base-url-override="baseUrlOverride"
        :realm-id="conversation.realmId"
        :link-name="linkName"
        :ti-chat-guest-disabled="tiChatGuestDisabled"
        @conversation-started="updateConversationStatus"
        @close-chat="closeChat"
        @offline-form-submitted="offlineFormSubmitted"
      ></ChatWindow>
    </div>
    <DefaultLauncher
      v-if="showLauncherIcon"
      :dealer-name="dealerName"
      :chat-window-maximized="chatWindowMaximized"
      :enable-transition="enableTransition"
      @close-chat="closeChat"
      @open-chat="openChat"
    >
    </DefaultLauncher>
  </div>
</template>

<script>
import ChatWindow from './ChatWindow.vue'
import DefaultLauncher from './launchers/DefaultLauncher.vue'
import ConversationUtil from '../utils/ConversationUtil.js'
import ChatApi from '../api/ChatAppApi.js'
export default {
  props: {
    chatAppUrl: {
      type: String,
      default: '',
    },
    dealerId: {
      type: String,
      default: '',
    },
    dealerName: {
      type: String,
      default: 'Someone',
    },
    adId: {
      type: String,
      default: '',
    },
    adUrl: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: 'default',
    },
    themeOverrides: {
      type: String,
      default: null,
    },
    popUpZIndex: {
      type: Number,
      default: 99998,
    },
    pageUrlOverride: {
      type: String,
    },
    baseUrlOverride: {
      type: String,
    },
    realmId: {
      type: Number,
      default: null,
    },
    showLauncher: {
      type: Boolean,
      default: true,
    },
    linkName: {
      type: String,
      default: 'tchat-onpage-link',
    },
    tiChatGuestDisabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChatWindow,
    DefaultLauncher,
  },
  created() {
    this.loadTheme()
    this.loadThemeOverrides()

    this.setupEventListeners()
  },
  data() {
    return {
      ChatState: 'launcher',
      ChatUtil: null,
      chatApi: null,
      showMessage: true,
      chatWindowMaximized: false,
      chatHasBeenInitialized: false,
      conversationStarted: false,
      enableTransition: true,
      conversation: null,
      lastDealerId: null,
      lastAdId: null,
      conversationSid: null,
    }
  },
  computed: {
    showLauncherIcon() {
      return this.showLauncher && ['launcher', 'minimized'].includes(this.ChatState)
    },
    showChatWindow() {
      return ['chat', 'submitted', 'minimized'].includes(this.ChatState)
    },
  },
  async mounted() {
    this.chatApi = new ChatApi(this.chatAppUrl)
  },
  methods: {
    toggleMessage() {
      this.showMessage = !this.showMessage
    },
    openChat(parameters = null) {
      const eventData = {
        adId: parameters && parameters.adId ? parameters.adId : this.adId,
        dealerId: parameters && parameters.dealerId ? parameters.dealerId : this.dealerId,
        dealerName: parameters && parameters.dealerName ? parameters.dealerName : this.dealerName,
        linkName: parameters && parameters.linkName ? parameters.linkName : this.linkName,
      }

      if (!this.chatHasBeenInitialized) {
        this.$events.triggerEvent('initial-click', eventData)
      }

      this.$events.triggerEvent('chat-click', eventData)

      switch (this.ChatState) {
        case 'chat':
          this.handleOpenFromChat(parameters)
          break
        case 'launcher':
          this.handleOpenFromLauncher(parameters)
          break
        case 'loading':
          this.handleOpenFromLoading(parameters)
          break
        case 'submitted':
          this.handleOpenFromSubmitted(parameters)
          break
        case 'minimized':
          this.handleOpenFromMinimized(parameters)
          break
      }
    },
    restartConversation(parameters) {
      this.$nextTick(() => {
        this.ChatState = 'chat'
      })

      this.createNewConversation(parameters)

      this.chatHasBeenInitialized = true
      this.chatWindowMaximized = true
    },
    handleOpenFromLauncher(parameters) {
      if (this.conversationStarted) {
        this.chatWindowMaximized = true
        this.ChatState = 'chat'
        return
      }

      this.ChatState = 'loading'
      this.restartConversation(parameters)
    },
    handleOpenFromChat(parameters) {
      if (this.conversationStarted) {
        if (confirm('Are you sure you would like to close the current chat?')) {
          this.conversationStarted = false
          this.ChatState = 'loading'
        } else {
          return
        }
      } else {
        this.ChatState = 'loading'
      }
      this.restartConversation(parameters)
    },
    handleOpenFromLoading() {},
    handleOpenFromMinimized(parameters) {
      if (parameters && this.lastDealerId != parameters.dealerId && this.lastAdId != parameters.adId) {
        this.handleOpenFromLauncher(parameters)
        return
      }
      this.chatWindowMaximized = true
      this.ChatState = 'chat'
    },
    handleOpenFromSubmitted(parameters) {
      this.conversationStarted = false
      this.ChatState = 'loading'
      this.restartConversation(parameters)
    },
    closeChat() {
      this.chatWindowMaximized = false
      this.ChatState = 'launcher'
      this.chatHasBeenInitialized = false
      this.conversationStarted = false
      this.conversation = null
      this.$events.triggerEvent('chat-closed', {
        adId: this.adId ? this.adId : '',
        dealerId: this.dealerId ? this.dealerId : '',
        dealerName: this.dealerName ? this.dealerName : '',
        linkName: this.linkName,
      })

      if (this.conversationSid) {
        this.chatApi.customerWindowClosed(this.conversationSid)
      }
    },
    updateConversationStatus(conversationSid) {
      this.conversationSid = conversationSid
      this.conversationStarted = true
      this.ChatState = 'chat'
    },
    offlineFormSubmitted() {
      this.ChatState = 'submitted'
    },
    createNewConversation(parameters = null) {
      let conversationParameters = {}

      conversationParameters.popUpZIndex = parameters?.popUpZIndex ?? this.popUpZIndex
      conversationParameters.dealerId = parameters?.dealerId ?? this.dealerId
      conversationParameters.dealerName = parameters?.dealerName ?? this.dealerName
      conversationParameters.adId = parameters?.adId ?? this.adId
      conversationParameters.adUrl = parameters?.adUrl ?? this.adUrl
      conversationParameters.pageUrlOverride = parameters?.pageUrlOverride ?? this.pageUrlOverride
      conversationParameters.realmId = parameters?.realmId ?? this.realmId

      this.conversation = new ConversationUtil(conversationParameters)
      this.lastAdId = parameters?.adId
      this.lastDealerId = parameters?.dealerId
    },
    setupEventListeners() {
      this.$events.addEventListener('open-chat', this.openChat)
      this.$events.addEventListener('close-chat', this.closeChat)
    },
    loadTheme() {
      this.$theme.setActiveTheme(this.theme)
      this.$styles.setTheme(this.$theme.getActiveTheme())
    },
    loadThemeOverrides() {
      if (this.themeOverrides !== null) {
        this.$theme.setThemeOverrides(JSON.parse(this.themeOverrides))
      }
    },
  },
}
</script>
